import {trpc} from '@/api/trpcClient';
import {useLocale} from '@/hooks/useLocale';
import {DEFAULT_CURRENCY, PortalClaim} from '@zentact/common';
import {useCallback, useMemo, useState} from 'react';
import {createContainer} from 'unstated-next';
import {AuthProfile} from '../types/auth-profile';

const Store = createContainer(() => {
  const {localeState, locale} = useLocale();
  const [publicTenantInfo, setPublicTenantInfo] = useState<{
    name: string;
    auth0OrganizationId: string;
  } | null>(null);

  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [authProfile, setAuthProfile] = useState<{
    isLoading: boolean;
    data?: AuthProfile;
    hasPermission(claim: PortalClaim): boolean;
  }>({
    isLoading: true,
    hasPermission: () => false,
  });

  // Get public tenant info on initial load
  trpc.tenant.getBasicTenant.useQuery(undefined, {
    refetchOnWindowFocus: false,
    onSuccess: data => {
      setPublicTenantInfo(data);
    },
    enabled: !accessToken,
  });

  const tenant = trpc.tenant.getContextTenant.useQuery(undefined, {
    refetchOnWindowFocus: false,
    enabled: !!accessToken,
  });

  const organization = trpc.organization.getContextOrganization.useQuery(undefined, {
    enabled: !!accessToken,
    refetchOnWindowFocus: true,
  });
  const pspMerchantAccountName = organization.data?.pspMerchantAccountName;

  const merchantAccounts = trpc.merchantAccount.getAllMerchantAccounts.useQuery(
    {
      // biome-ignore lint/style/noNonNullAssertion: checked in enabled condition
      pspMerchantAccountName: pspMerchantAccountName!,
    },
    {
      enabled: !!accessToken && !!pspMerchantAccountName,
      refetchOnWindowFocus: true,
      initialData: [],
    }
  );

  const {data: storesMissingBankAccountCount} =
    trpc.store.getStoresMissingBankAccountCount.useQuery(
      {
        // biome-ignore lint/style/noNonNullAssertion: checked in enabled condition
        pspMerchantAccountName: pspMerchantAccountName!,
      },
      {
        enabled:
          !!accessToken &&
          authProfile.hasPermission(PortalClaim.MANAGE_MERCHANTS) &&
          !!pspMerchantAccountName,
        refetchOnWindowFocus: true,
      }
    );

  const activeAndInactiveMerchantAccounts = useMemo(() => {
    return merchantAccounts.data.filter(
      merchant => merchant.status === 'ACTIVE' || merchant.status === 'INACTIVE'
    );
  }, [merchantAccounts.data]);

  const hasPermission = useCallback(
    (claim: PortalClaim) => {
      return authProfile.hasPermission(claim);
    },
    [authProfile]
  );

  const isAuthorizedDataLoading = !tenant || !organization || !pspMerchantAccountName;

  return {
    isAuthorizedDataLoading,
    isError: localeState.isError || tenant.isError,
    publicTenantInfo,
    tenant: tenant.data,
    organization: organization.data,
    pspMerchantAccountName: organization.data?.pspMerchantAccountName,
    organizationRefetch: organization.refetch,
    merchantAccountsRefetch: merchantAccounts.refetch,
    merchantAccounts: merchantAccounts.data,
    activeAndInactiveMerchantAccounts,
    authProfile,
    setAuthProfile,
    hasPermission,
    localeState,
    locale,
    setAccessToken,
    currency: organization.data?.currency || DEFAULT_CURRENCY,
    countryCode: organization.data?.countryCode || 'US',
    savedPaymentMethodsEnabled:
      !!organization.data?.organizationConfiguration?.savePaymentMethod &&
      organization.data.organizationConfiguration.savePaymentMethod !== 'DISABLED',
    storesMissingBankAccountCount,
  };
});

// should be used in PortalLayout children (regular portal pages)
export const useStore = () => {
  const {tenant, organization, pspMerchantAccountName, ...rest} = Store.useContainer();
  if (!tenant) {
    throw new Error('Store Error: Tenant not loaded');
  }
  if (!organization) {
    throw new Error('Store Error: organization not loaded');
  }
  if (!pspMerchantAccountName) {
    throw new Error('Store Error: pspMerchantAccountNames not loaded');
  }
  return {...rest, tenant, organization, pspMerchantAccountName};
};

// should be used in checkout and public pages that not require authorization
export const useStoreUnsafe = () => {
  return Store.useContainer();
};

export const StoreProvider = Store.Provider;
